import { Layout } from "providers/Layout";
import { useMemo, useState } from "react";
import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "providers/Auth";
import { useQuery } from "@tanstack/react-query";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Show
} from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Card } from "shared/components/Card";
import { Plus } from "untitledui-js-base";
import { AddIntegrationModal } from "views/settings-integration/components/AddIntegrationModal";
import { useSettingsController } from "views/settings-integration/controller";
import { PMSNames } from "./integrations/pms/constants";
import { IntegrationLogoIcon } from "views/settings-integration/components/IntegrationLogoIcon";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { PropertyManagementSystem } from "shared/types/partner";
import {
  getIntegrationCredentials,
  QUERY_INTEGRATION_CREDENTIALS
} from "api/landlord/integrations";
import NoIntegrationsIcon from "shared/icons/noIntegrations.svg?react";

const OTHER_INTEGRATION_IDS: Record<
  string,
  PropertyManagementSystem | IntegrationId
> = {
  HubSpot: IntegrationId.HUBSPOT,
  "Google Analytics": IntegrationId.GA
};

interface IntegrationsItem {
  enabled: boolean;
  title: string;
  description: string;
  link: string;
  icon?: ReactNode;
}

export default function IntegrationsView() {
  const [showAddIntegrationModal, setShowAddIntegrationModal] = useState(false);
  const navigate = useNavigate();
  const { partner, type } = useAuthContext();
  const { propertyManagementSystem, currentSettings, hubspotHubId } =
    useSettingsController();

  const { data: credentialsData } = useQuery({
    queryKey: [QUERY_INTEGRATION_CREDENTIALS],
    queryFn: getIntegrationCredentials,
    enabled: type.isMultiPMS
  });

  const integrationCredentials = credentialsData?.integration_credentials ?? [];

  const integrations: IntegrationsItem[] = useMemo(() => {
    const pmsTitle = propertyManagementSystem
      ? PMSNames[propertyManagementSystem]
      : "PMS"; //this is fallback which should not happen, added just to be safe

    return [
      {
        enabled: Boolean(hubspotHubId),
        title: "HubSpot",
        description: "Integration type: HubSpot",
        link: "/settings/integration/hubspot",
        icon: <IntegrationLogoIcon id={IntegrationId.HUBSPOT} size="BASE" />
      },
      {
        enabled: Boolean(currentSettings.google.googleAnalyticsId),
        title: "Google Analytics",
        description: "Integration type: Google Analytics",
        link: "/settings/integration/google-analytics",
        icon: <IntegrationLogoIcon id={IntegrationId.GA} size="BASE" />
      },
      {
        enabled: Boolean(propertyManagementSystem),
        title: pmsTitle,
        description: `Integration type: ${pmsTitle}`,
        link: "/settings/integration/pms",
        icon: propertyManagementSystem ? (
          <IntegrationLogoIcon id={propertyManagementSystem} size="BASE" />
        ) : undefined
      },
      ...(type.isBoomCRM && Boolean(partner?.codebox_connected)
        ? [
            {
              enabled: true,
              title: "Codebox",
              description: "Integration type: Codebox",
              link: "/settings/integration/codebox",
              icon: (
                <IntegrationLogoIcon id={IntegrationId.CODEBOX} size="BASE" />
              )
            }
          ]
        : []),
      ...(type.isBoomCRM && Boolean(partner?.zillow_connected)
        ? [
            {
              enabled: true,
              title: "Zillow",
              description: "Integration type: Zillow",
              link: "/settings/integration/zillow",
              icon: (
                <IntegrationLogoIcon id={IntegrationId.ZILLOW} size="BASE" />
              )
            }
          ]
        : []),
      ...(type.isBoomCRM && Boolean(partner?.apartments_com_connected)
        ? [
            {
              enabled: true,
              title: "Apartments.com",
              description: "Integration type: Apartments.com",
              link: "/settings/integration/apartments-com",
              icon: (
                <IntegrationLogoIcon
                  id={IntegrationId.APARTMENTS_COM}
                  size="BASE"
                />
              )
            }
          ]
        : [])
    ];
  }, [propertyManagementSystem, currentSettings, partner, type.isBoomCRM]);

  const pageHeader = (
    <>
      <div className="flex items-center justify-between border-b border-alpha-40 px-6 pb-[22px]">
        <BreadCrumbs
          crumbs={[
            {
              title: "Settings",
              href: "/settings"
            },
            {
              title: "Integration",
              href: "/settings/integration"
            }
          ]}
        />
      </div>
      <div className="flex items-center justify-between p-6 pb-3">
        <h1 className="text-xl font-bold text-content-1000">Integration</h1>
        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.SM}
          Icon={Plus}
          onClick={() => setShowAddIntegrationModal(true)}
        >
          Add integration
        </Button>
      </div>
    </>
  );

  type IntegrationTile = {
    type: "credential" | "integration";
    id: number | string;
    title: string;
    description: string;
    integrationId?: PropertyManagementSystem | IntegrationId;
    icon?: ReactNode;
    link: string;
  };

  const allIntegrationTiles = useMemo(() => {
    const tiles: IntegrationTile[] = [];

    // Add PMS credential tiles
    integrationCredentials.forEach((credential) => {
      const pmsLookup: Partial<Record<string, string>> = PMSNames;
      const pmsName = credential.integration
        ? (pmsLookup[credential.integration] ?? credential.integration)
        : "PMS";

      const integrationId:
        | PropertyManagementSystem
        | IntegrationId
        | undefined = credential.integration ?? undefined;

      tiles.push({
        type: "credential" as const,
        id: credential.id,
        title: credential.name,
        description: `Integration type: ${pmsName}`,
        integrationId,
        link: `/settings/integration/pms/${credential.id}`
      });
    });

    // Add other integration tiles (skip legacy PMS card)
    integrations.forEach((integration) => {
      if (
        integration.enabled &&
        integration.link !== "/settings/integration/pms"
      ) {
        const integrationId = OTHER_INTEGRATION_IDS[integration.title];
        tiles.push({
          type: "integration" as const,
          id: integration.title,
          title: integration.title,
          description: integration.description,
          icon: integration.icon,
          integrationId,
          link: integration.link
        });
      }
    });

    return tiles;
  }, [integrationCredentials, integrations]);

  if (type.isMultiPMS) {
    const hasAnyIntegrations = allIntegrationTiles.length > 0;

    return (
      <Layout
        headerClassName="border-b print:border-none border-alpha-100 bg-white pt-[22px]"
        header={pageHeader}
      >
        {!hasAnyIntegrations ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center justify-center gap-6 w-[424px]">
              {/* Illustration with circular background */}
              <div className="relative flex items-center justify-center size-[100px] rounded-[115px] bg-[rgba(28,29,34,0.06)]">
                <NoIntegrationsIcon className="text-[#1C1D22]" />
              </div>

              {/* Text content */}
              <div className="flex flex-col gap-2 items-center justify-center text-center">
                <p className="text-base font-bold text-content-700">
                  There&apos;s no integration yet
                </p>
                <p className="text-sm text-content-400 max-w-[360px]">
                  Add feature-rich two-way integration with you property
                  management software (PMS)
                </p>
              </div>

              {/* Button */}
              <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.BASE}
                Icon={Plus}
                onClick={() => setShowAddIntegrationModal(true)}
              >
                Add integration
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            {allIntegrationTiles.map((tile) => (
              <Card className="flex flex-col p-8" key={tile.id}>
                <div className="flex items-start justify-between">
                  {tile.integrationId ? (
                    <IntegrationLogoIcon id={tile.integrationId} size="BASE" />
                  ) : (
                    (tile.icon ?? <span />)
                  )}
                  <Badge variant={BadgeVariant.SUCCESS} size={BadgeSize.SM}>
                    Connected
                  </Badge>
                </div>

                <div className="mt-6 mb-4 h-full">
                  <div className="space-y-2">
                    <p className="font-bold text-base text-content-1000">
                      {tile.title}
                    </p>
                    <p className="text-sm text-content-400">
                      {tile.description}
                    </p>
                  </div>
                </div>

                <div className="flex border-t border-alpha-100 pt-4 items-end">
                  <Button
                    variant={ButtonVariant.TEXT}
                    size={ButtonSize.BASE}
                    onClick={() => navigate(tile.link)}
                  >
                    Configure
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        <Show if={showAddIntegrationModal}>
          <AddIntegrationModal
            opened={showAddIntegrationModal}
            onClose={() => setShowAddIntegrationModal(false)}
          />
        </Show>
      </Layout>
    );
  }

  // Legacy flow for non-multi-PMS partners
  return (
    <Layout
      headerClassName="border-b print:border-none border-alpha-100 bg-white pt-[22px]"
      header={pageHeader}
    >
      <div className="flex flex-wrap gap-4">
        {integrations.map((integration) => {
          if (!integration.enabled) return null;

          return (
            <Card
              className="flex flex-col p-8 w-[368px]"
              key={integration.title}
            >
              <div className="flex items-start justify-between">
                {integration.icon ?? <span />}
                <Badge variant={BadgeVariant.SUCCESS} size={BadgeSize.SM}>
                  Connected
                </Badge>
              </div>

              <div className="mt-6 mb-4 h-full">
                <div className="space-y-2">
                  <p className="font-bold text-base text-content-1000">
                    {integration.title}
                  </p>
                  <p className="text-sm text-content-400">
                    {integration.description}
                  </p>
                </div>
              </div>

              <div className="flex border-t border-alpha-100 pt-4 items-end">
                <Button
                  variant={ButtonVariant.TEXT}
                  size={ButtonSize.BASE}
                  onClick={() => navigate(integration.link)}
                >
                  Configure
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* We need this show to drop all states in modal when it closes */}
      <Show if={showAddIntegrationModal}>
        <AddIntegrationModal
          opened={showAddIntegrationModal}
          onClose={() => setShowAddIntegrationModal(false)}
        />
      </Show>
    </Layout>
  );
}
